import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {CAMPAIGN_APPLICATION_KEYS} from "@/api/campaignApplicationApi";
import {CHAT_KEYS} from "@/api/chatApi";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import DatePickerField from "@/components/Common/forms/DatePickerField";
import Modal from "@/components/Common/Modal";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import useCampaignInfluencerFilterStore from "@/store/campaignInfluencerFilter";
import {extendCampaignModalDataAtom} from "@/store/jotaiAtoms";
import {useMutation} from "@tanstack/react-query";
import {useParams} from "@tanstack/react-router";
import dayjs from "dayjs";
import {useAtom} from "jotai";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const dayDiff = (base: string | null | undefined, picked: Date | null) =>
  base && picked ? dayjs(picked).startOf("day").diff(dayjs(base).startOf("day"), "day") : 0;

const minSelectableDate = (base: string | null | undefined) => {
  const dayAfterBase = dayjs(base).startOf("day").add(1, "day");
  const today = dayjs().startOf("day");
  return (dayAfterBase.isAfter(today) ? dayAfterBase : today).toDate();
};

const ExtendCampaignModal: FC = () => {
  const {t} = useTranslation(["campaign_active", "common"]);
  const {influencerUserId} = useParams({strict: false});
  const searchParam = useCampaignInfluencerFilterStore(state => state.filters.search);
  const [extendCampaignModalData, setExtendCampaignModalData] = useAtom(extendCampaignModalDataAtom);
  const application = extendCampaignModalData?.application;
  const campaign = extendCampaignModalData?.campaign!;
  const mode = !!application ? "SINGLE_APPLICATION" : "ALL_APPLICATIONS";
  const isGatheringReporting = mode === "ALL_APPLICATIONS" && !!campaign.is_gathering_reporting;
  const mutationKey =
    mode === "SINGLE_APPLICATION" && !!application?.id
      ? CAMPAIGN_APPLICATION_KEYS.mApplicationExtendCompleted(application?.id)
      : CAMPAIGN_KEYS.mCampaignExtendDates(campaign.id);
  const [completedDate, setCompletedDate] = useState<Date | null>(null);
  const [preparationDate, setPreparationDate] = useState<Date | null>(null);

  const baseCompleted =
    mode === "SINGLE_APPLICATION" ? (application?.completed ?? campaign.completed) : campaign.completed;
  const completedDays = dayDiff(baseCompleted, completedDate);
  const preparationDays = mode === "ALL_APPLICATIONS" ? dayDiff(campaign.preparation, preparationDate) : 0;

  const {mutate: extend, isPending} = useMutation({
    mutationKey: mutationKey,
    mutationFn: () =>
      client.post(
        mutationKey[0],
        mode === "SINGLE_APPLICATION"
          ? {days: completedDays}
          : {completed_days: completedDays, preparation_days: preparationDays},
      ),
    onSuccess: () => {
      [
        CAMPAIGN_KEYS.qCampaign(campaign.id),
        CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id, searchParam),
        CAMPAIGN_KEYS.qCampaignManagement(campaign.id),
        CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id, false, searchParam),
        CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id, true, searchParam),
        CHAT_KEYS.qChatMessageList({campaignId: campaign.id, influencerUserId: Number(influencerUserId)}),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));
    },
  });

  const renderPhaseSection = (
    base: string | null | undefined,
    pickedDate: Date | null,
    onChange: (date: Date) => void,
    extensionDays: number,
    maxDate?: Date,
  ) => (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
      <div className="text-neutral-black flex flex-col gap-1 text-sm">
        <span className="font-semibold">{t("from")}</span>
        <span className="flex h-12 items-center">{dayjs(base).format("DD. MM. YYYY")}</span>
      </div>
      <div className="flex-1">
        <DatePickerField
          label={t("new_date")}
          value={pickedDate}
          onChange={onChange}
          minDate={minSelectableDate(base)}
          maxDate={maxDate}
          placeholder={t("select_date")}
        />
      </div>
      <span className="text-neutral-black flex h-12 items-center text-sm font-semibold">
        {extensionDays > 0 ? `+${extensionDays} ${t("common:days")}` : ""}
      </span>
    </div>
  );

  return (
    <Modal
      onClose={() => setExtendCampaignModalData(null)}
      isLoading={isPending}
      isConfirmDisabled={isPending || (completedDays < 1 && preparationDays < 1)}
      onConfirm={() => extend()}
      confirmButtonText={t("confirm_extension")}
      modalTitle={t(mode === "ALL_APPLICATIONS" ? "extend_campaign_for_everyone" : "extend_campaign_for_applicant")}
      backgroundClassName="max-w-150">
      <div className="flex flex-col gap-5">
        <span className="text-neutral-black text-sm">
          {t(
            mode === "ALL_APPLICATIONS"
              ? isGatheringReporting
                ? "for_outstanding_applicants"
                : campaign.ugc
                  ? "for_all_applicants_ugc"
                  : "for_all_applicants"
              : "for_applicant",
            {name: application?.first_name},
          )}
        </span>
        {mode === "ALL_APPLICATIONS" && !isGatheringReporting && (
          <>
            <span className="text-neutral-black text-sm font-semibold">{t("preparation_phase_extension")}</span>
            {renderPhaseSection(
              campaign.preparation,
              preparationDate,
              setPreparationDate,
              preparationDays,
              completedDate ?? (campaign.completed ? dayjs(campaign.completed).toDate() : undefined),
            )}
            <div className="bg-neutral-grey-light-dark h-px w-full" />
          </>
        )}
        <span className="text-neutral-black text-sm font-semibold">{t("active_phase_extension")}</span>
        {renderPhaseSection(baseCompleted, completedDate, setCompletedDate, completedDays)}
        {mode === "ALL_APPLICATIONS" && (
          <div className="border-warning-dark-yellow bg-warning-light-yellow text-warning-dark-yellow flex items-center gap-2 rounded-md border px-3 py-4">
            <WarningIcon className="size-4 shrink-0" />
            <span className="text-sm">
              {t(
                isGatheringReporting
                  ? "extend_campaign_gathering_reporting_warning"
                  : campaign.ugc
                    ? "extend_campaign_for_everyone_warning_ugc"
                    : "extend_campaign_for_everyone_warning",
              )}
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ExtendCampaignModal;
